'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sendGTMEvent } from '@next/third-parties/google';
import { TelegramProductExtendedDto, telegramProductsUserGetContactsQueryOptions } from '@/generated';
import { useModals } from '@/contexts/ModalContext';
import { useOpenLoginModal, useUserData } from '@/contexts/LoginContext';
import { WriteMessengerModal } from '@/modals/WriteToMessengerModal';
import { CallToUserModal } from '@/modals/CallToUserModal';
import { MakeSetPhoneModal } from '@/modals/MakeSetPhoneModal';

export function useProductContactActions(product: TelegramProductExtendedDto) {
  const { open, close } = useModals();
  const userData = useUserData();
  const queryClient = useQueryClient();
  const openLoginModal = useOpenLoginModal();
  const [guestLoading, setGuestLoading] = useState<'phone' | 'chat' | null>(null);
  const shouldOpenWrite = useRef<boolean | null>(false);

  const openPhoneNumberModal = useCallback(() => {
    open(
      'make-set-phone',
      <MakeSetPhoneModal
        onOk={() => {
          close('make-set-phone');
        }}
      />,
      true,
      'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
    );
  }, [open, close]);

  const openWriteMessengerModal = useCallback(() => {
    open(
      'write-messenger-modal',
      <WriteMessengerModal
        onOk={() => {
          close('write-messenger-modal');
        }}
        product={product}
      />,
      true,
      'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
    );
  }, [open, close, product]);

  const writeClickLoginedHandle = useCallback(async () => {
    if (!userData?.phoneNumber) {
      openPhoneNumberModal();
      return;
    }

    openWriteMessengerModal();
  }, [openPhoneNumberModal, openWriteMessengerModal, userData]);

  useEffect(() => {
    if (userData && userData.id && shouldOpenWrite.current === true) {
      shouldOpenWrite.current = false;
      writeClickLoginedHandle();
    }
  }, [userData, writeClickLoginedHandle]);

  const fetchGuestContacts = useCallback(
    async (contactAction: 'phone' | 'chat') => {
      setGuestLoading(contactAction);
      try {
        await queryClient.fetchQuery(
          telegramProductsUserGetContactsQueryOptions(product.link, { contactAction }, { handle401: false }),
        );
        return true;
      } catch {
        return false;
      } finally {
        setGuestLoading(null);
      }
    },
    [queryClient, product.link],
  );

  const onClickWrite = useCallback(async () => {
    sendGTMEvent({
      event: 'press_chat_button',
    });
    if (!userData) {
      if (await fetchGuestContacts('chat')) {
        openWriteMessengerModal();
        return;
      }
      openLoginModal(() => {
        shouldOpenWrite.current = true;
      }, 'messager');
      return;
    }

    writeClickLoginedHandle();
  }, [userData, writeClickLoginedHandle, openLoginModal, fetchGuestContacts, openWriteMessengerModal]);

  const openCallModal = useCallback(() => {
    open(
      'call-user-modal',
      <CallToUserModal
        onOk={() => {
          close('call-user-modal');
        }}
        product={product}
      />,
      true,
      'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
    );
  }, [open, close, product]);

  const onClickCall = async () => {
    sendGTMEvent({
      event: 'press_phone_button',
    });

    if (!userData) {
      if (await fetchGuestContacts('phone')) {
        openCallModal();
        return;
      }
      openLoginModal(openCallModal, 'messager');
      return;
    }

    openCallModal();
  };

  return { onClickWrite, onClickCall, guestLoading };
}
